/**
 * Binary Format API Client
 *
 * API service for managing binary format definitions.
 * Connects to the workflow backend service.
 */

import {
  buildWorkflowUserApiRoute,
} from '@/utils/workflow-utils';
import { workflowFetch } from '@/services/api/workflowApi';
import type {
  BinaryFormat,
  CreateBinaryFormatDTO,
  UpdateBinaryFormatDTO,
  BinaryFormatListFilters,
  PaginatedBinaryFormats,
  TestParseResponse,
  InlineBinaryFormat,
  ParseResult,
} from '@/types/binaryFormat';

/** Standard API response wrapper */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Binary Format API service
 */
export const binaryFormatApi = {
  // ================================
  // User-scoped Operations
  // ================================

  /**
   * List binary formats for a user
   */
  async listUserFormats(
    userId: string,
    filters?: BinaryFormatListFilters
  ): Promise<PaginatedBinaryFormats> {
    const params = new URLSearchParams();
    if (filters?.status) {
      params.append('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
    }
    if (filters?.tags?.length) {
      params.append('tags', filters.tags.join(','));
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.includeSystemTemplates) {
      params.append('includeSystemTemplates', 'true');
    }
    if (filters?.page) {
      params.append('page', String(filters.page));
    }
    if (filters?.pageSize) {
      params.append('pageSize', String(filters.pageSize));
    }
    if (filters?.sort) {
      params.append('sort', filters.sort);
    }
    if (filters?.order) {
      params.append('order', filters.order);
    }

    const url = `${buildWorkflowUserApiRoute('binary-formats', userId)}?${params.toString()}`;
    const response = await workflowFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch formats' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat[]> = await response.json();
    return {
      items: result.data,
      total: result.pagination?.total ?? result.data.length,
      page: result.pagination?.page ?? 1,
      pageSize: result.pagination?.limit ?? 20,
      totalPages: result.pagination?.totalPages ?? 1,
      hasNext: result.pagination?.hasNext ?? false,
      hasPrev: result.pagination?.hasPrev ?? false,
    };
  },

  /**
   * Get a single binary format by ID
   */
  async getFormat(userId: string, formatId: string): Promise<BinaryFormat> {
    const url = buildWorkflowUserApiRoute(`binary-formats/${formatId}`, userId);
    const response = await workflowFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Format not found' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat> = await response.json();
    return result.data;
  },

  /**
   * Create a new binary format
   */
  async createFormat(userId: string, data: CreateBinaryFormatDTO): Promise<BinaryFormat> {
    const url = buildWorkflowUserApiRoute('binary-formats', userId);
    const response = await workflowFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to create format' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat> = await response.json();
    return result.data;
  },

  /**
   * Update an existing binary format
   */
  async updateFormat(
    userId: string,
    formatId: string,
    data: UpdateBinaryFormatDTO
  ): Promise<BinaryFormat> {
    const url = buildWorkflowUserApiRoute(`binary-formats/${formatId}`, userId);
    const response = await workflowFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update format' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat> = await response.json();
    return result.data;
  },

  /**
   * Delete a binary format
   */
  async deleteFormat(userId: string, formatId: string): Promise<void> {
    const url = buildWorkflowUserApiRoute(`binary-formats/${formatId}`, userId);
    const response = await workflowFetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete format' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
  },

  /**
   * Publish a draft format (draft → active)
   */
  async publishFormat(userId: string, formatId: string): Promise<BinaryFormat> {
    const url = buildWorkflowUserApiRoute(`binary-formats/${formatId}/publish`, userId);
    const response = await workflowFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to publish format' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat> = await response.json();
    return result.data;
  },

  /**
   * Deprecate an active format (active → deprecated)
   */
  async deprecateFormat(userId: string, formatId: string): Promise<BinaryFormat> {
    const url = buildWorkflowUserApiRoute(`binary-formats/${formatId}/deprecate`, userId);
    const response = await workflowFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to deprecate format' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat> = await response.json();
    return result.data;
  },

  /**
   * Get all versions of a format
   */
  async getFormatVersions(userId: string, formatId: string): Promise<BinaryFormat[]> {
    const url = buildWorkflowUserApiRoute(`binary-formats/${formatId}/versions`, userId);
    const response = await workflowFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch versions' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat[]> = await response.json();
    return result.data;
  },

  /**
   * Test a saved format with sample data
   */
  async testFormat(
    userId: string,
    formatId: string,
    sampleData: string,
    inputFormat?: string
  ): Promise<TestParseResponse> {
    const url = buildWorkflowUserApiRoute(`binary-formats/${formatId}/test`, userId);
    const response = await workflowFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sampleData, inputFormat }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to test format' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<TestParseResponse> = await response.json();
    return result.data;
  },

  /**
   * Clone a system template
   */
  async cloneTemplate(
    userId: string,
    templateId: string,
    name?: string
  ): Promise<BinaryFormat> {
    const url = buildWorkflowUserApiRoute(`binary-formats/clone/${templateId}`, userId);
    const response = await workflowFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to clone template' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat> = await response.json();
    return result.data;
  },

  // ================================
  // Utility Operations (no owner scope)
  // ================================

  /**
   * Get system templates
   */
  async getSystemTemplates(): Promise<BinaryFormat[]> {
    const response = await workflowFetch('api/binary-formats/templates', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch templates' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<BinaryFormat[]> = await response.json();
    return result.data;
  },

  /**
   * Preview parse with inline format (no save)
   */
  async previewParse(
    format: InlineBinaryFormat,
    sampleData: string
  ): Promise<TestParseResponse> {
    const response = await workflowFetch('api/binary-formats/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ format, sampleData }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to preview format' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<TestParseResponse> = await response.json();
    return result.data;
  },

  /**
   * Validate format definition without saving
   */
  async validateFormat(format: InlineBinaryFormat): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  }> {
    const response = await workflowFetch('api/binary-formats/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(format),
    });

    const result = await response.json();
    return result.data;
  },

  /**
   * Decode binary data and return hex/decimal dumps
   */
  async decodeData(
    data: string,
    inputFormat: 'base64' | 'hex' | 'binary' | 'raw'
  ): Promise<{
    hexDump: string;
    decimalDump: number[];
    byteCount: number;
    hexString: string;
  }> {
    const response = await workflowFetch('api/binary-formats/decode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data, inputFormat }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to decode data' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result: ApiResponse<{
      hexDump: string;
      decimalDump: number[];
      byteCount: number;
      hexString: string;
    }> = await response.json();
    return result.data;
  },
};

export default binaryFormatApi;
